import { AfterViewInit, Component, Inject, OnDestroy } from '@angular/core';
import {
	BehaviorSubject,
	interval,
	Observable,
	of,
	debounce,
	mergeMap,
	Subscription,
	from,
	map,
} from 'rxjs';
import {
	MatDatepickerInputEvent,
	MatDatepickerInput,
	MatDatepickerToggle,
	MatDatepicker,
} from '@angular/material/datepicker';
import {
	MatDialogRef,
	MAT_DIALOG_DATA,
	MatDialogContent,
	MatDialogActions,
} from '@angular/material/dialog';
import { Router } from '@angular/router';
import { FeatureFlagName } from 'src/app/models/data/featureFlag';
import {
	CodeGenReportFilterOptions,
	CodeGenReportFilters,
	emptyCodeGenReportFilters,
} from 'src/app/models/data/reports';
import { UserProfile } from 'src/app/models/model';
import {
	ApiService,
	QueryParam,
	ApiLoadingKeys,
} from 'src/app/services/api.services';
import { Client } from '../../../models/client';
import { UserProfileService } from '../../../services/user-profile.service';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import {
	MatFormField,
	MatLabel,
	MatSuffix,
} from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDivider } from '@angular/material/list';
import { LoadingIndicatorComponent } from '../../../components/loading-indicator/loading-indicator.component';
import { MultiSelectTypeaheadComponent } from '../../../components/multi-select-typeahead/multi-select-typeahead.component';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';

interface SearchConfig {
	type:
		| 'device'
		| 'deviceOwner'
		| 'generatedBy'
		| 'assignedTo'
		| 'firstName'
		| 'lastName'
		| 'clientIdn';
	searchEvents: BehaviorSubject<string>;
	subscription: Subscription;
	selectedItems: Set<any>;
	options: any[];
	apiCall: (params: QueryParam[]) => Promise<any>;
	mapResponse: (item: any) => any;
	updateFilters: (items: any[]) => void;
}

@Component({
	selector: 'app-code-gen-report-dialog',
	templateUrl: './code-gen-report-dialog.component.html',
	styleUrls: [
		'./code-gen-report-dialog.component.scss',
		'../../common/main.scss',
	],
	imports: [
		MatIconButton,
		MatIcon,
		CdkScrollable,
		MatDialogContent,
		MatFormField,
		MatSelect,
		MatOption,
		MatTooltip,
		MatDivider,
		LoadingIndicatorComponent,
		MultiSelectTypeaheadComponent,
		MatLabel,
		MatInput,
		MatDatepickerInput,
		FormsModule,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepicker,
		MatDialogActions,
		MatButton,
	],
})
export class CodeGenReportDialogComponent implements AfterViewInit, OnDestroy {
	codeGenReportFilters: CodeGenReportFilters = emptyCodeGenReportFilters();
	isHaspCodeGenFeatureEnabled: boolean = false;

	filterOptions?: CodeGenReportFilterOptions;

	apiLoadingKeys = ApiLoadingKeys;

	deviceOptions?: { id: string; serialNumber: string }[] = [];
	deviceOwnerOptions?: { id: string; name: string }[] = [];
	generatedByUserOptions?: {
		id: string;
		firstName: string;
		lastName: string;
		email: string;
		displayName: string;
	}[] = [];
	assignedToUserOptions?: {
		id: string;
		firstName: string;
		lastName: string;
		email: string;
		displayName: string;
	}[] = [];
	firstNames?: Client[] = [];
	lastNames?: Client[] = [];
	clientIdns?: Client[] = [];

	activeCompanyId = '';
	isSentrilock = false;
	isCareCompany = false;

	deviceId: string[] = [];
	deviceOwnerId: string[] = [];
	generatedById: string[] = [];
	assignedToId: string[] = [];
	firstName: string[] = [];
	lastName: string[] = [];
	clientIdn: string[] = [];

	deviceMultiSelectSearchPlaceholder = $localize`:@@codeGenReportDialog-deviceMultiSelectSearchPlaceholder: Search Serial Numbers (optional)`;
	deviceOwnerMultiSelectSearchPlaceholder = $localize`:@@codeGenReportDialog-deviceOwnerMultiSelectSearchPlaceholder: Search Companies (optional)`;
	generatedByMultiSelectSearchPlaceholder = $localize`:@@codeGenReportDialog-generatedByMultiSelectSearchPlaceholder: Search Users (optional)`;
	userNameOrEmailPlaceHolder = $localize`:@@codeGenReportDialog-userNameOrEmailPlaceHolder: User name or email`;
	firstNameMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-firstNameMultiSelectSearchPlaceholder: Search Client First Names (optional)`;
	lastNameMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-lastNameMultiSelectSearchPlaceholder: Search Client Last Names (optional)`;
	clientIdnMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-clientIdnMultiSelectSearchPlaceholder: Search Client IDNs (optional)`;

	accessCodeTooltip = $localize`:@@codeGenReportDialog-accessCodeTooltip:Access Code`;
	haspCodeTooltip = $localize`:@@codeGenReportDialog-haspCodeTooltip:Hasp Code`;

	private readonly searchConfigs: { [key: string]: SearchConfig } = {
		device: {
			type: 'device',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.deviceOptions || [],
			apiCall: (params) => this.apiService.getAllDevices(params),
			mapResponse: (device) => ({
				id: device.device.id,
				serialNumber: device.device.serialNumber,
			}),
			updateFilters: (items) => {
				this.codeGenReportFilters.device = [...items];
			},
		},
		deviceOwner: {
			type: 'deviceOwner',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.deviceOwnerOptions || [],
			apiCall: (params) => this.apiService.getCompanyFilterOptions(params),
			mapResponse: (company) => ({
				id: company.id,
				name: company.name,
			}),
			updateFilters: (items) => {
				this.codeGenReportFilters.deviceOwner = [...items];
			},
		},
		generatedBy: {
			type: 'generatedBy',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.generatedByUserOptions || [],
			apiCall: (params) =>
				this.apiService.getCompanyUsersByCompanyId(
					this.activeCompanyId,
					params
				),
			mapResponse: (user) => ({
				id: user.user.id,
				firstName: user.user.firstName,
				lastName: user.user.lastName,
				email: user.user.email,
				displayName: `${user.user.firstName}  ${user.user.lastName} (${user.user.email})`,
			}),
			updateFilters: (items) => {
				this.codeGenReportFilters.generatedBy = [...items];
			},
		},
		assignedTo: {
			type: 'assignedTo',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.assignedToUserOptions || [],
			apiCall: (params) =>
				this.apiService.getCompanyUsersByCompanyId(
					this.activeCompanyId,
					params
				),
			mapResponse: (user) => ({
				id: user.user.id,
				firstName: user.user.firstName,
				lastName: user.user.lastName,
				email: user.user.email,
				displayName: `${user.user.firstName}  ${user.user.lastName} (${user.user.email})`,
			}),
			updateFilters: (items) => {
				this.codeGenReportFilters.assignedTo = [...items];
			},
		},
		firstName: {
			type: 'firstName',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.firstNames || [],
			apiCall: (params) =>
				this.apiService.getClients(this.activeCompanyId, params),
			mapResponse: (client) => client,
			updateFilters: (items) => {
				this.codeGenReportFilters.firstName = [...items];
			},
		},
		lastName: {
			type: 'lastName',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.lastNames || [],
			apiCall: (params) =>
				this.apiService.getClients(this.activeCompanyId, params),
			mapResponse: (client) => client,
			updateFilters: (items) => {
				this.codeGenReportFilters.lastName = [...items];
			},
		},
		clientIdn: {
			type: 'clientIdn',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.clientIdns || [],
			apiCall: (params) =>
				this.apiService.getClients(this.activeCompanyId, params),
			mapResponse: (client) => client,
			updateFilters: (items) => {
				this.codeGenReportFilters.clientIdn = [...items];
			},
		},
	};

	constructor(
		private readonly dialogRef: MatDialogRef<CodeGenReportDialogComponent>,
		private readonly apiService: ApiService,
		private readonly router: Router,
		private readonly userProfileService: UserProfileService,
		@Inject(MAT_DIALOG_DATA)
		public data?: {
			reportFilters: CodeGenReportFilters;
			filterOptions: CodeGenReportFilterOptions;
		}
	) {
		this.filterOptions = data?.filterOptions;
		if (data?.reportFilters) {
			this.codeGenReportFilters = data.reportFilters;
			this.codeGenReportFilters.device?.forEach((device) => {
				this.deviceId.push(device.id);
			});
			this.codeGenReportFilters.deviceOwner?.forEach((deviceOwner) => {
				this.deviceOwnerId.push(deviceOwner.id);
			});
			this.codeGenReportFilters.generatedBy?.forEach((generatedBy) => {
				this.generatedById.push(generatedBy.id);
			});
			// assignedTo is an optional field so using email as the id
			this.codeGenReportFilters.assignedTo?.forEach((assignedTo) => {
				this.assignedToId.push(assignedTo.email);
			});
		}
		this.initializeData();
		Object.keys(this.searchConfigs).forEach((key) => this.setupSearch(key));
		this.featureFlagCheck();
	}

	async featureFlagCheck(): Promise<void> {
		this.isHaspCodeGenFeatureEnabled = (
			await this.apiService.getFeatureFlagByName(FeatureFlagName.webHaspCodes)
		)?.value;
	}

	async ngAfterViewInit(): Promise<void> {
		const userProfile: UserProfile = await this.apiService.getUserProfile();
		this.activeCompanyId = userProfile?.activeRoleDetails?.company?.id ?? '';
		const sentrilockId = await this.apiService.getSentrilockCompanyId();
		this.isSentrilock = this.activeCompanyId === sentrilockId;
	}

	get hasFilters(): boolean {
		return (
			JSON.stringify(this.codeGenReportFilters) !==
			JSON.stringify(emptyCodeGenReportFilters())
		);
	}

	get primaryButtonText(): string {
		return this.hasFilters
			? $localize`:@@codeGenReportDialog-generate:Generate`
			: $localize`:@@codeGenReportDialog-generateWithoutFilters:Generate without Filters`;
	}

	onSelectedCodeTypeEvent(codeTypeId: any): void {
		if (codeTypeId === undefined) {
			return;
		}
		this.codeGenReportFilters.codeTypeId = codeTypeId;
	}

	onSelectedDevicesEvent(device: any): void {
		if (device === undefined) {
			return;
		}
		this.codeGenReportFilters.device = [...device];
	}

	onSelectedDeviceOwnerEvent(deviceOwner: any): void {
		if (deviceOwner === undefined) {
			return;
		}
		this.codeGenReportFilters.deviceOwner = [...deviceOwner];
	}

	onSelectedGeneratedByEvent(generatedBy: any): void {
		if (generatedBy === undefined) {
			return;
		}
		this.codeGenReportFilters.generatedBy = [...generatedBy];
	}

	onSelectedAssignedToEvent(assignedTo: any): void {
		if (assignedTo === undefined) {
			return;
		}
		this.codeGenReportFilters.assignedTo = [...assignedTo];
	}

	onDateGeneratedStartDateChanged(event: MatDatepickerInputEvent<Date>): void {
		this.codeGenReportFilters.dateGeneratedStartDate = event.value ?? undefined;
	}

	onDateGeneratedEndDateChanged(event: MatDatepickerInputEvent<Date>): void {
		this.codeGenReportFilters.dateGeneratedEndDate = event.value ?? undefined;
	}

	onDateValidStartDateChanged(event: MatDatepickerInputEvent<Date>): void {
		this.codeGenReportFilters.dateValidStartDate = event.value ?? undefined;
	}

	onDateValidEndDateChanged(event: MatDatepickerInputEvent<Date>): void {
		this.codeGenReportFilters.dateValidEndDate = event.value ?? undefined;
	}

	onCloseDialog(): void {
		this.dialogRef.close();
	}

	async onGenerateReport(): Promise<void> {
		const compiledFilters = this.compileFilters();
		this.router
			.navigateByUrl(`/reports/code-gen-report`, {
				state: {
					reportFilters: this.codeGenReportFilters,
					compiledFilters,
					hasFilters: this.hasFilters,
					filterOptions: this.filterOptions,
				},
			})
			.then(() => {
				this.dialogRef.close();
			});
	}

	compileFilters(): QueryParam[] {
		const queryParameters: QueryParam[] = [];

		if (
			this.isHaspCodeGenFeatureEnabled &&
			this.codeGenReportFilters.codeTypeId
		) {
			queryParameters.push({
				param: 'codeTypeId',
				value: this.codeGenReportFilters.codeTypeId,
			});
		}

		if (
			this.codeGenReportFilters.device &&
			this.codeGenReportFilters.device.length > 0
		) {
			queryParameters.push({
				param: 'deviceIds',
				value: this.codeGenReportFilters.device.map((it) => it.id),
			});
		}

		if (
			this.codeGenReportFilters.deviceOwner &&
			this.codeGenReportFilters.deviceOwner.length > 0
		) {
			queryParameters.push({
				param: 'deviceOwnerIds',
				value: this.codeGenReportFilters.deviceOwner.map((it) => it.id),
			});
		}

		if (
			this.codeGenReportFilters.generatedBy &&
			this.codeGenReportFilters.generatedBy.length > 0
		) {
			queryParameters.push({
				param: 'generatedByIds',
				value: this.codeGenReportFilters.generatedBy.map((it) => it.id),
			});
		}

		if (
			this.codeGenReportFilters.assignedTo &&
			this.codeGenReportFilters.assignedTo.length > 0
		) {
			queryParameters.push({
				param: 'assignedTo',
				value: this.codeGenReportFilters.assignedTo.map((it) => it.email),
			});
		}

		if (this.codeGenReportFilters.dateGeneratedStartDate) {
			queryParameters.push({
				param: 'dateGeneratedUtcStart',
				value: this.codeGenReportFilters.dateGeneratedStartDate
					.toISOString()
					.split('T')[0],
			});
		}

		if (this.codeGenReportFilters.dateGeneratedEndDate) {
			queryParameters.push({
				param: 'dateGeneratedUtcEnd',
				value: this.codeGenReportFilters.dateGeneratedEndDate
					.toISOString()
					.split('T')[0],
			});
		}

		if (this.codeGenReportFilters.dateValidStartDate) {
			queryParameters.push({
				param: 'dateValidUtcStart',
				value: this.codeGenReportFilters.dateValidStartDate
					.toISOString()
					.split('T')[0],
			});
		}

		if (this.codeGenReportFilters.dateValidEndDate) {
			queryParameters.push({
				param: 'dateValidUtcEnd',
				value: this.codeGenReportFilters.dateValidEndDate
					.toISOString()
					.split('T')[0],
			});
		}

		if (
			this.codeGenReportFilters.firstName &&
			this.codeGenReportFilters.firstName.length > 0
		) {
			queryParameters.push({
				param: 'firstNames',
				value: this.codeGenReportFilters.firstName.map(
					(it) => it.firstName || ''
				),
			});
		}

		if (
			this.codeGenReportFilters.lastName &&
			this.codeGenReportFilters.lastName.length > 0
		) {
			queryParameters.push({
				param: 'lastNames',
				value: this.codeGenReportFilters.lastName.map(
					(it) => it.lastName || ''
				),
			});
		}

		if (
			this.codeGenReportFilters.clientIdn &&
			this.codeGenReportFilters.clientIdn.length > 0
		) {
			queryParameters.push({
				param: 'clientIdns',
				value: this.codeGenReportFilters.clientIdn.map((it) => it.idn || ''),
			});
		}

		return queryParameters;
	}

	private initializeData(): void {
		this.filterOptions = this.data?.filterOptions;
		if (this.isSentrilock) {
			this.deviceOwnerOptions = this.filterOptions?.ownerCompanies;
		}

		if (this.data?.reportFilters) {
			this.codeGenReportFilters = this.data.reportFilters;

			// Initialize deviceOptions with previous selections
			if (this.codeGenReportFilters.device?.length) {
				this.codeGenReportFilters.device.forEach((device) => {
					const deviceOption = {
						id: device.id,
						serialNumber: device.serialNumber,
					};
					this.deviceId.push(device.id);
					this.searchConfigs.device.selectedItems.add(deviceOption);
				});

				// Initialize deviceOptions with selected devices
				this.deviceOptions = Array.from(
					this.searchConfigs.device.selectedItems
				);
			}

			// Initialize deviceOwnerOptions with previous selections
			if (this.codeGenReportFilters.deviceOwner?.length) {
				this.codeGenReportFilters.deviceOwner.forEach((company) => {
					const companyOption = {
						id: company.id,
						name: company.name,
					};
					this.deviceOwnerId.push(company.id);
					this.searchConfigs.deviceOwner.selectedItems.add(companyOption);
				});
				// Initialize deviceOwnerOptions with selected companies
				this.deviceOwnerOptions = Array.from(
					this.searchConfigs.deviceOwner.selectedItems
				);
			}

			// Initialize accessedByUserOptions with previous selections
			if (this.codeGenReportFilters.assignedTo?.length) {
				this.codeGenReportFilters.assignedTo.forEach((user) => {
					const userOption = {
						id: user.id,
						firstName: user.firstName,
						lastName: user.lastName,
						email: user.email,
						displayName: `${user.firstName} ${user.lastName} (${user.email})`,
					};
					this.assignedToId.push(user.id);
					this.searchConfigs.assignedTo.selectedItems.add(userOption);
				});
				// Initialize accessedByUserOptions with selected users
				this.assignedToUserOptions = Array.from(
					this.searchConfigs.assignedTo.selectedItems
				);
			}

			// Initialize accessedByUserOptions with previous selections
			if (this.codeGenReportFilters.generatedBy?.length) {
				this.codeGenReportFilters.generatedBy?.forEach((user) => {
					const userOption = {
						id: user.id,
						firstName: user.firstName,
						lastName: user.lastName,
						email: user.email,
						displayName: `${user.firstName} ${user.lastName} (${user.email})`,
					};
					this.generatedById.push(user.id);
					this.searchConfigs.generatedBy.selectedItems.add(userOption);
				});
				// Initialize accessedByUserOptions with selected users
				this.generatedByUserOptions = Array.from(
					this.searchConfigs.generatedBy.selectedItems
				);
			}
		}

		this.checkCareCompany()
			.then(() => {})
			.catch((error) =>
				console.error(
					'Something went wrong while checking care company:',
					error.message
				)
			);
	}

	async checkCareCompany(): Promise<void> {
		const userProfile = await this.userProfileService.getUserProfile();
		this.isCareCompany =
			userProfile?.activeRoleDetails?.company?.companyType === 'care';
	}

	private setupSearch(type: string): void {
		const config = this.searchConfigs[type];
		const searchResults = config.searchEvents.asObservable().pipe(
			debounce(() => interval(300)),
			mergeMap((searchTerm) => this.handleSearch(searchTerm, config))
		);

		config.subscription = searchResults.subscribe();
	}

	private handleSearch(
		searchTerm: string,
		config: SearchConfig
	): Observable<any[]> {
		if (['firstName', 'lastName', 'clientIdn'].includes(config.type)) {
			if (!searchTerm || searchTerm.length < 2) {
				switch (config.type) {
					case 'firstName':
						return of(this.firstNames || []);
					case 'lastName':
						return of(this.lastNames || []);
					case 'clientIdn':
						return of(this.clientIdns || []);
				}
			}
		} else {
			if (!searchTerm || searchTerm === '' || searchTerm.length < 3) {
				switch (config.type) {
					case 'device':
						return of(this.deviceOptions || []);
					case 'deviceOwner':
						return of(this.deviceOwnerOptions || []);
					case 'generatedBy':
						return of(this.generatedByUserOptions || []);
					case 'assignedTo':
						return of(this.assignedToUserOptions || []);
				}
			}
		}

		const queryParameters: QueryParam[] = [
			{ param: this.getSearchParamName(config), value: searchTerm },
			{ param: 'rowIndex', value: 0 },
			{ param: 'rowCount', value: 100 },
		];

		return from(config.apiCall(queryParameters)).pipe(
			map((results) => {
				if (!results?.length) {
					return Array.from(config.selectedItems);
				}

				const newOptions = results.map(config.mapResponse);
				const mergedOptions = this.mergeWithSelected(
					newOptions,
					config.selectedItems
				);

				// Update the corresponding options array based on the config type
				switch (config.type) {
					case 'device':
						this.deviceOptions = mergedOptions;
						break;
					case 'firstName':
						this.firstNames = mergedOptions;
						break;
					case 'lastName':
						this.lastNames = mergedOptions;
						break;
					case 'clientIdn':
						this.clientIdns = mergedOptions;
						break;
					case 'deviceOwner':
						this.deviceOwnerOptions = mergedOptions;
						break;
					case 'generatedBy':
						this.generatedByUserOptions = mergedOptions;
						break;
					case 'assignedTo':
						this.assignedToUserOptions = mergedOptions;
						break;
				}

				return mergedOptions;
			})
		);
	}
	private getSearchParamName(config: SearchConfig): string {
		switch (config.type) {
			case 'device':
				return 'serialNumber';
			case 'firstName':
				return 'firstName';
			case 'lastName':
				return 'lastName';
			case 'clientIdn':
				return 'idn';
			case 'deviceOwner':
				return 'companyName';
			case 'generatedBy':
				return 'name';
			case 'assignedTo':
				return 'name';
			default:
				return 'name';
		}
	}

	private mergeWithSelected(newOptions: any[], selectedItems: Set<any>): any[] {
		if (!newOptions) newOptions = [];
		if (!selectedItems) return newOptions;

		const newOptionIds = new Set(newOptions.map((opt) => opt.id));
		let mergedOptions = [...newOptions];

		selectedItems.forEach((selected) => {
			if (newOptionIds.has(selected.id)) {
				// if the selected item is already in the new options list, remove it from mergedOptions
				mergedOptions = mergedOptions.filter((opt) => opt.id !== selected.id);
			}
		});

		return mergedOptions;
	}

	onSearchInput(type: string, searchTerm: string): void {
		this.searchConfigs[type].searchEvents.next(searchTerm);
	}

	onSelectedItemsChanged(type: string, items: any): void {
		if (!items) return;

		const config = this.searchConfigs[type];
		config.selectedItems = new Set(items);
		config.updateFilters(items);
		config.searchEvents.next('');
	}

	ngOnDestroy(): void {
		Object.values(this.searchConfigs).forEach((config) => {
			if (config.subscription) {
				config.subscription.unsubscribe();
			}
		});
	}
}
